import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaFileExcel,
  FaFileCsv,
  FaFilter,
  FaSearch,
  FaTimes,
  FaSync,
} from "react-icons/fa";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { API_URL } from "../../config";
import { useNavigate } from "react-router-dom";

const VisitorAnalytics = () => {
  const [visitorData, setVisitorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("visitor_name");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Office filter
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("all");

  // Location filters
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedAssembly, setSelectedAssembly] = useState("");

  const navigate = useNavigate();

  const handleViewDetails = (visitor_id) => {
    navigate(`/visitor-details/${visitor_id}`);
  };

  // 🔹 Fetch offices
  const fetchOffices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}getOfficesList`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === "success") {
        setOffices(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    }
  };

  // 🔹 Fetch states and locations
  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: null, dist_name1: null },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setStates(response.data.states || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  // 🔹 Fetch districts
  const fetchDistricts = async (state) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setDistricts(response.data.districts || []);
        setAssemblies([]);
        setSelectedDistrict("");
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  // 🔹 Fetch assemblies
  const fetchAssemblies = async (state, district) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state, dist_name1: district },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAssemblies(response.data.assemblies || []);
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching assemblies:", error);
    }
  };

  // 🔹 Handle state change
  const handleStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);

    if (state) {
      fetchDistricts(state);
    }
  };

  // 🔹 Handle district change
  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setSelectedDistrict(district);
    setSelectedAssembly("");
    setAssemblies([]);

    if (district && selectedState) {
      fetchAssemblies(selectedState, district);
    }
  };

  // 🔹 Fetch visitor data
  const fetchVisitorData = async (page = 1, fetchAll = false) => {
    setLoading(true);
    let allData = [];

    try {
      const token = localStorage.getItem("token");

      const params = {
        status: statusFilter !== "all" ? statusFilter : "",
        start_date: startDate || "",
        end_date: endDate || "",
        page: page.toString(),
        perPage: perPage.toString(),
        search_query: searchQuery || "",
        search_type: searchType,
      };

      // Add location filters if selected
      if (selectedState) {
        params.state = selectedState;
      }
      if (selectedDistrict) {
        params.district = selectedDistrict;
      }
      if (selectedAssembly) {
        params.assembly = selectedAssembly;
      }
      if (selectedOffice !== "all") {
        params.office_id = selectedOffice;
      }

      if (fetchAll) {
        let currentPage = 1;
        while (true) {
          params.page = currentPage.toString();

          const response = await axios.post(
            `${API_URL}getReportbystatus`,
            new URLSearchParams(params).toString(),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/x-www-form-urlencoded",
              },
            }
          );

          if (
            response.data.status === "success" &&
            response.data.data.length > 0
          ) {
            allData = [...allData, ...response.data.data];
            if (response.data.data.length < perPage) break;
            currentPage++;
          } else {
            break;
          }
        }
      } else {
        const response = await axios.post(
          `${API_URL}getReportbystatus`,
          new URLSearchParams(params).toString(),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.status === "success") {
          setVisitorData(response.data.data || []);
          const totalRecords = response.data.count || 0;
          setTotalPages(Math.ceil(totalRecords / perPage));

          if (response.data.data.length === 0 && page > 1) {
            setCurrentPage(1);
          }
        } else {
          setVisitorData([]);
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error("Error fetching visitor data:", error);
      setVisitorData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      if (fetchAll) return allData;
    }
  };

  // Fetch offices and locations once
  useEffect(() => {
    fetchOffices();
    fetchLocations();
  }, []);

  // Fetch visitors on filters change
  useEffect(() => {
    fetchVisitorData(currentPage);
  }, [
    currentPage,
    statusFilter,
    startDate,
    endDate,
    selectedOffice,
    selectedState,
    selectedDistrict,
    selectedAssembly,
    searchQuery,
    searchType,
  ]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getFollowUpStatus = (status) => {
    const statusMap = {
      open: "Pending",
      "on-going": "In Progress",
      close: "Complete",
    };
    return statusMap[status] || "Unknown";
  };

  const getStatusBadgeClass = (status) => {
    const badgeClasses = {
      open: "bg-yellow-100 text-yellow-800",
      "on-going": "bg-blue-100 text-blue-800",
      close: "bg-green-100 text-green-800",
    };
    return badgeClasses[status] || "bg-gray-100 text-gray-800";
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchVisitorData(1);
  };

  // Handle reset
  const handleReset = () => {
    setSearchQuery("");
    setSearchType("visitor_name");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setSelectedOffice("all");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);
    setCurrentPage(1);
    fetchVisitorData(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleExport = async (type) => {
    setLoading(true);
    const allVisitorData = await fetchVisitorData(1, true);
    setLoading(false);

    if (allVisitorData.length === 0) {
      alert("No data available to export.");
      return;
    }

    switch (type) {
      case "PDF":
        exportToPDF(allVisitorData);
        break;
      case "Excel":
        exportToExcel(allVisitorData);
        break;
      case "CSV":
        exportToCSV(allVisitorData);
        break;
      default:
        break;
    }
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Visitor Analytics Report", 14, 10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 18);

    const headers = [
      [
        "Visitor ID",
        "Name",
        "Mobile",
        "Email",
        "Status",
        "Category",
        "State",
        "District",
        "Assembly",
        "Created Date",
      ],
    ];

    const body = data.map((visitor) => [
      visitor.visitor_id,
      visitor.visitor_name,
      visitor.mobile_number,
      visitor.email_id || "N/A",
      getFollowUpStatus(visitor.status),
      visitor.categorie_name || "Unknown",
      visitor.state || "N/A",
      visitor.district || "N/A",
      visitor.assembly || "N/A",
      formatDate(visitor.created_at),
    ]);

    doc.autoTable({
      head: headers,
      body: body,
      startY: 25,
      theme: "grid",
      headStyles: { fillColor: [6, 136, 0] },
    });
    doc.save("visitor_analytics_report.pdf");
  };

  const exportToExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((visitor) => ({
        "Visitor ID": visitor.visitor_id,
        Name: visitor.visitor_name,
        Mobile: visitor.mobile_number,
        Email: visitor.email_id || "N/A",
        Status: getFollowUpStatus(visitor.status),
        Category: visitor.categorie_name || "Unknown",
        State: visitor.state || "N/A",
        District: visitor.district || "N/A",
        Assembly: visitor.assembly || "N/A",
        "Created Date": formatDate(visitor.created_at),
        Office: visitor.office?.name || "N/A",
        Query: visitor.query || "N/A",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Visitor Analytics");

    // Auto-size columns
    const maxWidths = {};
    XLSX.utils.sheet_to_json(worksheet, { header: 1 }).forEach((row) => {
      row.forEach((cell, colIndex) => {
        const cellLength = cell ? cell.toString().length : 0;
        maxWidths[colIndex] = Math.max(maxWidths[colIndex] || 0, cellLength);
      });
    });

    worksheet["!cols"] = Object.keys(maxWidths).map((col) => ({
      width: maxWidths[col] + 2,
    }));

    XLSX.writeFile(workbook, "visitor_analytics.xlsx");
  };

  const exportToCSV = (data) => {
    const csv = Papa.unparse(
      data.map((visitor) => ({
        "Visitor ID": visitor.visitor_id,
        Name: visitor.visitor_name,
        Mobile: visitor.mobile_number,
        Email: visitor.email_id || "N/A",
        Status: getFollowUpStatus(visitor.status),
        Category: visitor.categorie_name || "Unknown",
        State: visitor.state || "N/A",
        District: visitor.district || "N/A",
        Assembly: visitor.assembly || "N/A",
        "Created Date": formatDate(visitor.created_at),
        Office: visitor.office?.name || "N/A",
        Query: visitor.query || "N/A",
      }))
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "visitor_analytics.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Visitor Analytics
            </h1>
            <p className="text-gray-600 mt-2">
              Comprehensive visitor reporting and analytics
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchVisitorData(currentPage)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <FaSync className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <FaFilter className="w-5 h-5 text-gray-600" />
          <h2 className="text-xl font-bold text-gray-800">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Search Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Search By
            </label>
            <div className="flex gap-2">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="w-1/3 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="visitor_name">Name</option>
                <option value="mobile_number">Mobile</option>
                <option value="traking_id">Tracking ID</option>
              </select>
              <input
                type="text"
                placeholder={`Search by ${
                  searchType === "visitor_name"
                    ? "name"
                    : searchType === "mobile_number"
                    ? "mobile"
                    : "tracking ID"
                }...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="open">Pending</option>
              <option value="on-going">In Progress</option>
              <option value="close">Resolved</option>
            </select>
          </div>

          {/* Office Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Office</label>
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Offices</option>
              {offices.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* State Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">State</label>
            <select
              value={selectedState}
              onChange={handleStateChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All States</option>
              {states.map((state, index) => (
                <option key={index} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          {/* District Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              District
            </label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              disabled={!selectedState}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">All Districts</option>
              {districts.map((district, index) => (
                <option key={index} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </div>

          {/* Assembly Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Assembly
            </label>
            <select
              value={selectedAssembly}
              onChange={(e) => setSelectedAssembly(e.target.value)}
              disabled={!selectedDistrict}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">All Assemblies</option>
              {assemblies.map((assembly, index) => (
                <option key={index} value={assembly}>
                  {assembly}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleSearch}
            className="px-6 py-3 bg-gradient-to-r from-[#068800] to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <FaSearch className="w-5 h-5" />
            Search
          </button>
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
          >
            <FaTimes className="w-5 h-5" />
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Issues</p>
              <p className="text-2xl font-bold text-gray-800">
                {visitorData.filter((v) => v.status === "open").length}
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-yellow-500 font-bold">P</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-gray-800">
                {visitorData.filter((v) => v.status === "on-going").length}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-500 font-bold">IP</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-2xl font-bold text-gray-800">
                {visitorData.filter((v) => v.status === "close").length}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-500 font-bold">R</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Visitors</p>
              <p className="text-2xl font-bold text-gray-800">
                {visitorData.length}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-500 font-bold">T</span>
            </div>
          </div>
        </div>
      </div>

      {/* Visitor Data Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Visitor Analytics
              </h2>
              <p className="text-gray-600 text-sm">
                Showing {visitorData.length} records
              </p>
            </div>
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#068800] mb-4"></div>
            <p className="text-gray-600">Loading visitor data...</p>
          </div>
        ) : visitorData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Visitor Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Contact & Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status & Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visitorData.map((visitor) => (
                  <tr
                    key={visitor.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {visitor.visitor_name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <span className="font-medium">ID:</span>{" "}
                          {visitor.visitor_id}
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          Created: {formatDate(visitor.created_at)}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="font-medium">
                          {visitor.mobile_number}
                        </div>
                        <div className="text-sm text-gray-600">
                          {visitor.state || "N/A"}
                          {visitor.district && `, ${visitor.district}`}
                          {visitor.assembly && `, ${visitor.assembly}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {visitor.office?.name || "No Office"}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                            visitor.status
                          )}`}
                        >
                          {getFollowUpStatus(visitor.status)}
                        </span>
                        <div className="text-sm text-gray-700">
                          {visitor.categorie_name || "Unknown Category"}
                        </div>
                        {visitor.traking_id && (
                          <div className="text-xs text-gray-600 font-mono">
                            Track: {visitor.traking_id}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewDetails(visitor.visitor_id)}
                        className="px-4 py-2 bg-gradient-to-r from-[#068800] to-green-600 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 text-sm font-medium shadow-sm hover:shadow"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 text-gray-300">
              <FaSearch className="w-full h-full" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Records Found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search filters or date range
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Showing {visitorData.length} records
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg font-medium ${
                          currentPage === pageNum
                            ? "bg-[#068800] text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Export Buttons */}
      <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">
          Export Analytics
        </h3>
        <p className="text-gray-600 mb-6">
          Export the filtered data in your preferred format
        </p>

        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={() => handleExport("PDF")}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <span className="font-bold">PDF</span>
            Export as PDF
          </button>

          <button
            onClick={() => handleExport("Excel")}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <FaFileExcel className="w-5 h-5" />
            Export as Excel
          </button>

          <button
            onClick={() => handleExport("CSV")}
            className="px-6 py-3 bg-gradient-to-r from-[#068800] to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <FaFileCsv className="w-5 h-5" />
            Export as CSV
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalytics;
